<template>
  <div>
    <router-view v-if="render" />
    <ConnectionStatus />

    <!-- SOCKET NOTIFICATIONS -->
    <div
      class="pointer-events-none fixed bottom-5 right-5 z-[99999] flex w-[calc(100%-2rem)] max-w-sm flex-col items-end gap-2 sm:w-full"
      aria-live="polite"
      aria-atomic="false"
    >
      <!-- QUEUED COUNT -->
      <transition name="socket-notification-fade">
        <div
          v-if="socketNotificationQueue.length > 0"
          class="rounded-full border border-green-600 bg-green-600 px-3.5 py-2 text-xs font-bold text-white shadow-lg"
        >
          +{{ socketNotificationQueue.length }}
          {{
            socketNotificationQueue.length === 1
              ? "notification"
              : "notifications"
          }}
        </div>
      </transition>

      <!-- MAXIMUM OF 2 VISIBLE NOTIFICATIONS -->
      <transition-group
        name="socket-notification-stack"
        tag="div"
        class="flex w-full flex-col gap-2"
      >
        <div
          v-for="notification in visibleSocketNotifications"
          :key="notification.id"
          class="pointer-events-auto relative overflow-hidden rounded-xl border border-emerald-300 bg-white shadow-xl ring-1 ring-black/5"
        >
          <!-- LEFT ACCENT -->
          <div class="absolute inset-y-0 left-0 w-1 bg-emerald-500"></div>

          <div class="flex items-start gap-3 py-4 pl-5 pr-4">
            <!-- STATUS ICON -->
            <div
              class="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200"
            >
              <svg
                viewBox="0 0 24 24"
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
            </div>

            <!-- CONTENT -->
            <div class="min-w-0 flex-1 pr-5">
              <p class="text-sm font-bold text-gray-900">
                {{ notification.title }}
              </p>

              <p
                v-if="notification.message"
                class="mt-1 break-words text-xs leading-5 text-gray-600"
              >
                {{ notification.message }}
              </p>

              <button
                v-if="notification.route"
                type="button"
                class="mt-3 inline-flex items-center rounded-md bg-green-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-green-700 active:scale-[0.98]"
                @click="openSocketNotificationRoute(notification)"
              >
                View details
              </button>
            </div>

            <!-- CLOSE -->
            <button
              type="button"
              class="absolute right-2.5 top-2.5 inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              title="Close notification"
              @click="dismissSocketNotification(notification.id)"
            >
              <svg
                viewBox="0 0 24 24"
                class="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6 6 18"></path>
              </svg>
            </button>
          </div>

          <!-- PROGRESS -->
          <div class="h-1 w-full bg-gray-100">
            <div
              class="socket-notification-progress h-full bg-emerald-500"
              :style="{ animationDuration: `${notification.duration}ms` }"
            ></div>
          </div>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import { socket, connect } from "@/socket";
import pingSound from "@/assets/audio/ping_sound.mp3";
import axios from "axios";
import ConnectionStatus from "./views/ConnectionStatus.vue";

export default {
  data() {
    return {
      userId: null,
      audio: null,
      audioUnlocked: false,
      render: true,

      // Socket notification stack
      activeSocketNotifications: [],
      socketNotificationQueue: [],
      socketNotificationCounter: 0,
      socketNotificationTimers: {},
      maxVisibleSocketNotifications: 2,
      socketNotificationDuration: 15000,
    };
  },

  components: {
    ConnectionStatus,
  },

  computed: {
    /*
     * Newest visible notification appears above the older one.
     *
     * Example:
     * +3 notifications
     * Notification #2
     * Notification #1
     */
    visibleSocketNotifications() {
      return [...this.activeSocketNotifications].reverse();
    },
  },

  mounted() {
    this.audio = new Audio(pingSound);

    window.addEventListener(
      "click",
      () => {
        this.audioUnlocked = true;
      },
      { once: true }
    );

    const storedUserId = this.$store?.state?.user?.id;

    if (storedUserId) {
      this.userId = storedUserId;

      connect(this.userId);

      socket.off("ping");
      socket.off("connect");
      socket.off("disconnect");

      socket.on("ping", (data) => {
        const notificationType = String(
          data?.notificationType || ""
        )
          .trim()
          .toLowerCase();

        const isSilentCommentUpdate = [
          "comment_edit",
          "comment_delete",
        ].includes(notificationType);

        if (!isSilentCommentUpdate) {
          this.showSocketNotification(data);
        }

        /*
         * Notify mounted notification components that a new persistent
         * notification has been received. The navbar and NotificationsView
         * will refresh their count and notification list from the backend.
         */
        window.dispatchEvent(
          new CustomEvent("notifications-updated", {
            detail: {
              source: "socket-ping",
              notificationId:
                data?.notificationId || null,
              notificationType:
                data?.notificationType || null,
              route:
                data?.route || null,
            },
          })
        );

        if (!isSilentCommentUpdate) {
          this.playPingSound();
        }
      });

      socket.on("connect", () => {
        console.log("Socket connected:", socket.id);

        if (this.userId) {
          socket.emit("join", this.userId);
          console.log(
            "Re-joined room for user:",
            this.userId
          );
        }
      });

      socket.on("disconnect", (reason) => {
        console.warn(
          "Socket disconnected:",
          reason
        );
      });
    }
  },

  beforeUnmount() {
    this.clearSocketNotificationTimers();
  },

  methods: {
    showSocketNotification(data) {
      const notification = {
        id: ++this.socketNotificationCounter,
        title:
          String(data?.title || "").trim() ||
          "New Notification",
        message:
          String(data?.message || "").trim(),
        route:
          data?.route || null,
        duration:
          this.socketNotificationDuration,
      };

      if (
        this.activeSocketNotifications.length <
        this.maxVisibleSocketNotifications
      ) {
        this.activateSocketNotification(
          notification
        );
      } else {
        this.socketNotificationQueue.push(
          notification
        );
      }
    },

    activateSocketNotification(notification) {
      this.activeSocketNotifications.push(
        notification
      );

      this.socketNotificationTimers[
        notification.id
      ] = window.setTimeout(() => {
        this.dismissSocketNotification(
          notification.id
        );
      }, notification.duration);
    },

    dismissSocketNotification(id) {
      const timer =
        this.socketNotificationTimers[id];

      if (timer) {
        window.clearTimeout(timer);

        delete this.socketNotificationTimers[
          id
        ];
      }

      const activeIndex =
        this.activeSocketNotifications.findIndex(
          (notification) =>
            Number(notification.id) ===
            Number(id)
        );

      if (activeIndex !== -1) {
        this.activeSocketNotifications.splice(
          activeIndex,
          1
        );

        this.$nextTick(() => {
          this.showNextQueuedSocketNotification();
        });

        return;
      }

      const queueIndex =
        this.socketNotificationQueue.findIndex(
          (notification) =>
            Number(notification.id) ===
            Number(id)
        );

      if (queueIndex !== -1) {
        this.socketNotificationQueue.splice(
          queueIndex,
          1
        );
      }
    },

    showNextQueuedSocketNotification() {
      if (
        this.activeSocketNotifications.length >=
          this.maxVisibleSocketNotifications ||
        this.socketNotificationQueue.length ===
          0
      ) {
        return;
      }

      const nextNotification =
        this.socketNotificationQueue.shift();

      if (nextNotification) {
        this.activateSocketNotification(
          nextNotification
        );
      }

      if (
        this.activeSocketNotifications.length <
          this.maxVisibleSocketNotifications &&
        this.socketNotificationQueue.length > 0
      ) {
        this.showNextQueuedSocketNotification();
      }
    },

    clearSocketNotificationTimers() {
      Object.values(
        this.socketNotificationTimers
      ).forEach((timer) => {
        window.clearTimeout(timer);
      });

      this.socketNotificationTimers = {};
    },

    openSocketNotificationRoute(
      notification
    ) {
      const route = notification?.route;

      if (!route) {
        return;
      }

      if (typeof route === "string") {
        const routePath = route.startsWith("/")
          ? route
          : `/${route}`;

        this.$router
          .push(routePath)
          .catch(() => {});

        return;
      }

      if (route.name) {
        this.$router
          .push({
            name: route.name,
            query: route.query || {},
            params: route.params || {},
          })
          .catch(() => {});

        return;
      }

      if (route.path) {
        this.$router
          .push({
            path: route.path,
            query: route.query || {},
          })
          .catch(() => {});
      }
    },

    getUser() {
      const localToken =
        localStorage.getItem("token");

      if (!localToken) {
        this.render = true;
        localStorage.clear();
        this.$router.replace("/");
        return;
      }

      this.render = false;

      console.log(
        "TOKEN SENT TO AUTH ME:",
        localToken
      );

      axios
        .get(
          process.env.VUE_APP_BASE_URL +
            "/auth/me",
          {
            headers: {
              Authorization:
                `Bearer ${localToken}`,
            },
          }
        )
        .then((res) => {
          const user = res.data.data;

          this.$store.dispatch(
            "setUser",
            user
          );

          localStorage.setItem(
            "user_id",
            user.id
          );

          this.$store.commit(
            "UPDATE_USER",
            user
          );

          const publicPages = [
            "/",
            "/login",
          ];

          if (
            publicPages.includes(
              this.$route.path
            )
          ) {
            this.$router.replace(
              "/dashboard"
            );
          }
        })
        .catch((err) => {
          console.log(err);
          localStorage.clear();
          this.$router.replace("/");
        })
        .finally(() => {
          this.render = true;
        });
    },

    playPingSound() {
      if (
        !this.audioUnlocked ||
        !this.audio
      ) {
        return;
      }

      try {
        this.audio.currentTime = 0;

        this.audio
          .play()
          .catch(() => {});
      } catch (error) {
        console.warn(
          "Audio play failed:",
          error
        );
      }
    },
  },
};
</script>

<style>
/* Socket notification stack animations */
.socket-notification-stack-enter-active,
.socket-notification-stack-leave-active,
.socket-notification-stack-move {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.socket-notification-stack-enter-from,
.socket-notification-stack-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.socket-notification-stack-leave-active {
  position: absolute;
  width: 100%;
}

.socket-notification-fade-enter-active,
.socket-notification-fade-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.socket-notification-fade-enter-from,
.socket-notification-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

/* Auto-close progress */
.socket-notification-progress {
  width: 100%;
  transform-origin: left center;
  animation-name:
    socketNotificationProgress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes socketNotificationProgress {
  from {
    transform: scaleX(1);
  }

  to {
    transform: scaleX(0);
  }
}
</style>
